import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Truck,
  KeyRound,
  CheckCircle2,
  XCircle,
  PlugZap,
  Loader2,
  Info,
  ChevronDown,
  Settings2,
} from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AVAILABLE_SHIPPING_OPTIONS } from "@/lib/shipping";
import { ShippingProviderCards, FutureProviderCards } from "@/components/shipping-provider-cards";
import {
  getSellerShipping,
  saveSellerShipping,
  saveRoyalMailConnection,
  testRoyalMailConnection,
  disconnectRoyalMail,
} from "@/lib/seller-shipping.functions";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/seller/shipping")({
  head: () => ({ meta: [{ title: "Shipping preferences — BidNova Auctions" }] }),
  component: ShippingSettingsPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

type Settings = {
  sender_name?: string | null;
  sender_email?: string | null;
  sender_phone?: string | null;
  sender_line1?: string | null;
  sender_line2?: string | null;
  sender_city?: string | null;
  sender_county?: string | null;
  sender_postcode?: string | null;
  sender_country?: string | null;
  default_weight_grams: number;
  default_package_format: string;
  default_service: string;
  status?: string;
  api_key_last4?: string | null;
  last_validated_at?: string | null;
  last_validation_error?: string | null;
};

function ShippingSettingsPage() {
  const { t } = useTranslation("settings");
  const { user } = useAuth();
  const qc = useQueryClient();

  const PACKAGE_FORMATS = [
    { id: "letter", label: t("shipping.formatLetter", "Letter") },
    { id: "largeLetter", label: t("shipping.formatLargeLetter", "Large Letter") },
    { id: "smallParcel", label: t("shipping.formatSmallParcel", "Small Parcel") },
    { id: "mediumParcel", label: t("shipping.formatMediumParcel", "Medium Parcel") },
    { id: "parcel", label: t("shipping.formatParcel", "Parcel") },
  ];

  const getFn = useServerFn(getSellerShipping);
  const saveFn = useServerFn(saveSellerShipping);
  const connectFn = useServerFn(saveRoyalMailConnection);
  const testFn = useServerFn(testRoyalMailConnection);
  const disconnectFn = useServerFn(disconnectRoyalMail);

  const { data: current, isLoading } = useQuery({
    queryKey: ["seller-shipping", user?.id],
    enabled: !!user,
    queryFn: () => getFn({ data: undefined } as unknown as Parameters<typeof getFn>[0]),
  });

  const [form, setForm] = useState<Settings>({
    default_weight_grams: 100,
    default_package_format: "smallParcel",
    default_service: "royal_mail_tracked_48",
    sender_country: "GB",
  });
  const [apiKey, setApiKey] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  useEffect(() => {
    if (current) {
      const c = current as unknown as Settings;
      setForm({
        sender_name: c.sender_name ?? "",
        sender_email: c.sender_email ?? "",
        sender_phone: c.sender_phone ?? "",
        sender_line1: c.sender_line1 ?? "",
        sender_line2: c.sender_line2 ?? "",
        sender_city: c.sender_city ?? "",
        sender_county: c.sender_county ?? "",
        sender_postcode: c.sender_postcode ?? "",
        sender_country: c.sender_country ?? "GB",
        default_weight_grams: c.default_weight_grams ?? 100,
        default_package_format: c.default_package_format ?? "smallParcel",
        default_service: c.default_service ?? "royal_mail_tracked_48",
        status: c.status,
        api_key_last4: c.api_key_last4,
        last_validated_at: c.last_validated_at,
        last_validation_error: c.last_validation_error,
      });
      // Auto-open advanced when the seller has already connected or a check failed.
      if (c.status === "connected" || c.status === "invalid" || c.api_key_last4) {
        setAdvancedOpen(true);
      }
    }
  }, [current]);

  const save = useMutation({
    mutationFn: async () => {
      await saveFn({
        data: {
          sender_name: form.sender_name || null,
          sender_email: form.sender_email || null,
          sender_phone: form.sender_phone || null,
          sender_line1: form.sender_line1 || null,
          sender_line2: form.sender_line2 || null,
          sender_city: form.sender_city || null,
          sender_county: form.sender_county || null,
          sender_postcode: form.sender_postcode || null,
          sender_country: form.sender_country || "GB",
          default_weight_grams: Number(form.default_weight_grams) || 100,
          default_package_format: form.default_package_format,
          default_service: form.default_service,
        },
      });
    },
    onSuccess: () => {
      toast.success(t("shipping.saveSuccess", "Shipping preferences saved"));
      qc.invalidateQueries({ queryKey: ["seller-shipping"] });
    },
    onError: () => toast.error(t("shipping.saveError", "We couldn't save your shipping preferences. Please try again.")),
  });

  const connect = useMutation({
    mutationFn: async () => await connectFn({ data: { apiKey: apiKey.trim() } }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t("shipping.connectSuccess", "Royal Mail Click & Drop connected"));
      } else {
        toast.error(t("shipping.connectErrorTitle", "Connection unsuccessful"), {
          description: t("shipping.connectErrorDesc", "We couldn't verify your Royal Mail Click & Drop account. Please check your API key and try again."),
        });
      }
      setApiKey("");
      qc.invalidateQueries({ queryKey: ["seller-shipping"] });
    },
    onError: () =>
      toast.error(t("shipping.connectErrorTitle", "Connection unsuccessful"), {
        description: t("shipping.connectErrorDesc", "We couldn't verify your Royal Mail Click & Drop account. Please check your API key and try again."),
      }),
  });

  const test = useMutation({
    mutationFn: async () => await testFn({ data: undefined } as unknown as Parameters<typeof testFn>[0]),
    onSuccess: (r) => {
      if (r.ok) toast.success(t("shipping.testSuccess", "Royal Mail connection is valid"));
      else
        toast.error(t("shipping.connectErrorTitle", "Connection unsuccessful"), {
          description: t("shipping.connectErrorDesc", "We couldn't verify your Royal Mail Click & Drop account. Please check your API key and try again."),
        });
      qc.invalidateQueries({ queryKey: ["seller-shipping"] });
    },
    onError: () =>
      toast.error(t("shipping.connectErrorTitle", "Connection unsuccessful"), {
        description: t("shipping.connectErrorDesc", "We couldn't verify your Royal Mail Click & Drop account. Please check your API key and try again."),
      }),
  });

  const disconnect = useMutation({
    mutationFn: async () => await disconnectFn({ data: undefined } as unknown as Parameters<typeof disconnectFn>[0]),
    onSuccess: () => {
      toast.success(t("shipping.disconnectSuccess", "Royal Mail disconnected"));
      qc.invalidateQueries({ queryKey: ["seller-shipping"] });
    },
    onError: () => toast.error(t("shipping.disconnectError", "We couldn't disconnect Royal Mail. Please try again.")),
  });

  const updateSender = (patch: Partial<Settings>) => setForm((f) => ({ ...f, ...patch }));

  const status = form.status ?? "disconnected";
  const isConnected = status === "connected";
  const isInvalid = status === "invalid";

  const statusBadge = isConnected ? (
    <Badge className="bg-emerald-600/20 text-emerald-300 border-emerald-600/40">
      <CheckCircle2 className="h-3 w-3 mr-1" aria-hidden="true" />
      {t("shipping.statusConnected", "Connected")}
    </Badge>
  ) : isInvalid ? (
    <Badge className="bg-red-600/20 text-red-300 border-red-600/40">
      <XCircle className="h-3 w-3 mr-1" aria-hidden="true" />
      {t("shipping.statusUnsuccessful", "Connection unsuccessful")}
    </Badge>
  ) : (
    <Badge variant="outline">{t("shipping.statusNotConnected", "Not connected")}</Badge>
  );

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-10">
      <Link
        to="/seller"
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden="true" /> {t("shipping.backToSeller", "Back to Seller Dashboard")}
      </Link>

      <header className="mb-8">
        <p className="text-xs uppercase tracking-widest text-gold">{t("shipping.eyebrow", "Shipping")}</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl flex items-center gap-2">
          <Truck className="h-7 w-7 sm:h-8 sm:w-8 shrink-0" aria-hidden="true" />
          <span className="min-w-0">{t("shipping.title", "Shipping preferences")}</span>
        </h1>
        <p className="mt-2 text-muted-foreground">
          {t("shipping.subtitle", "Manage your preferred shipping options for BidNova listings. You do not need to connect Royal Mail to sell on BidNova.")}
        </p>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {t("shipping.loading", "Loading your shipping preferences…")}
        </div>
      ) : (
        <div className="space-y-8">
          {/* ------------------------------------------------------------------ */}
          {/* SECTION 1 — Shipping preferences                                   */}
          {/* ------------------------------------------------------------------ */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
            className="space-y-6 rounded-xl border border-border/60 p-5 sm:p-6"
            aria-labelledby="prefs-heading"
          >
            <div>
              <h2 id="prefs-heading" className="font-display text-xl flex items-center gap-2">
                <Settings2 className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="min-w-0">{t("shipping.prefsHeading", "Your shipping preferences")}</span>
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("shipping.prefsDesc", "Set the default parcel and service used when you create a new listing.")}
              </p>
            </div>

            <div
              role="note"
              className="flex items-start gap-2 rounded-md border border-border/50 bg-background/40 px-3 py-2 text-xs text-muted-foreground"
            >
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              <p>
                {t("shipping.noteOptional", "Royal Mail Click & Drop is optional. Sellers may choose any supported shipping service when publishing a listing.")}
              </p>
            </div>

            <ShippingProviderCards />

            <section className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="pref-service">{t("shipping.preferredService", "Preferred shipping service")}</Label>
                <Select
                  value={form.default_service}
                  onValueChange={(v) => updateSender({ default_service: v })}
                >
                  <SelectTrigger id="pref-service" className="min-h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_SHIPPING_OPTIONS.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("shipping.preferredServiceHelp", "Used as the default on new listings. You can override this per listing.")}
                </p>
              </div>

              <div>
                <Label htmlFor="pref-weight">{t("shipping.defaultWeight", "Default parcel weight (grams)")}</Label>
                <Input
                  id="pref-weight"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={30000}
                  className="min-h-11"
                  value={form.default_weight_grams}
                  onChange={(e) => updateSender({ default_weight_grams: Number(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="pref-format">{t("shipping.defaultFormat", "Default package type")}</Label>
                <Select
                  value={form.default_package_format}
                  onValueChange={(v) => updateSender({ default_package_format: v })}
                >
                  <SelectTrigger id="pref-format" className="min-h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PACKAGE_FORMATS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            <section
              aria-labelledby="future-prefs-heading"
              className="rounded-md border border-dashed border-border/60 bg-background/30 p-4 space-y-2"
            >
              <h3 id="future-prefs-heading" className="text-sm font-medium">
                {t("shipping.futureHeading", "More preferences coming soon")}
              </h3>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                <li>{t("shipping.futureDispatch", "Dispatch time (how quickly you post after a sale)")}</li>
                <li>{t("shipping.futureDomestic", "Domestic shipping preference")}</li>
                <li>{t("shipping.futureCollection", "Collection preference")}</li>
              </ul>
              <p className="text-[11px] text-muted-foreground">
                {t("shipping.futureNote", "These options aren't available yet. Set your dispatch expectations in each listing description for now.")}
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="font-display text-lg">{t("shipping.senderHeading", "Sender / return details")}</h3>
              <p className="text-xs text-muted-foreground">
                {t("shipping.senderDesc", "Used on shipping labels and return addresses when applicable.")}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="sender-name">{t("shipping.senderName", "Trading / sender name")}</Label>
                  <Input
                    id="sender-name"
                    className="min-h-11"
                    value={form.sender_name ?? ""}
                    onChange={(e) => updateSender({ sender_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sender-email">{t("shipping.senderEmail", "Contact email")}</Label>
                  <Input
                    id="sender-email"
                    type="email"
                    autoComplete="email"
                    className="min-h-11"
                    value={form.sender_email ?? ""}
                    onChange={(e) => updateSender({ sender_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sender-phone">{t("shipping.senderPhone", "Contact phone")}</Label>
                  <Input
                    id="sender-phone"
                    type="tel"
                    autoComplete="tel"
                    className="min-h-11"
                    value={form.sender_phone ?? ""}
                    onChange={(e) => updateSender({ sender_phone: e.target.value })}
                  />
                </div>
                <div className="hidden sm:block" aria-hidden="true" />
                <div className="sm:col-span-2">
                  <Label htmlFor="sender-line1">{t("shipping.senderLine1", "Address line 1")}</Label>
                  <Input
                    id="sender-line1"
                    autoComplete="address-line1"
                    className="min-h-11"
                    value={form.sender_line1 ?? ""}
                    onChange={(e) => updateSender({ sender_line1: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="sender-line2">{t("shipping.senderLine2", "Address line 2")}</Label>
                  <Input
                    id="sender-line2"
                    autoComplete="address-line2"
                    className="min-h-11"
                    value={form.sender_line2 ?? ""}
                    onChange={(e) => updateSender({ sender_line2: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sender-city">{t("shipping.senderCity", "City")}</Label>
                  <Input
                    id="sender-city"
                    autoComplete="address-level2"
                    className="min-h-11"
                    value={form.sender_city ?? ""}
                    onChange={(e) => updateSender({ sender_city: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sender-county">{t("shipping.senderCounty", "County")}</Label>
                  <Input
                    id="sender-county"
                    autoComplete="address-level1"
                    className="min-h-11"
                    value={form.sender_county ?? ""}
                    onChange={(e) => updateSender({ sender_county: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="sender-postcode">{t("shipping.senderPostcode", "Postcode")}</Label>
                  <Input
                    id="sender-postcode"
                    autoComplete="postal-code"
                    className="min-h-11"
                    value={form.sender_postcode ?? ""}
                    onChange={(e) => updateSender({ sender_postcode: e.target.value.toUpperCase() })}
                  />
                </div>
                <div>
                  <Label htmlFor="sender-country">{t("shipping.senderCountry", "Country")}</Label>
                  <Input
                    id="sender-country"
                    autoComplete="country"
                    maxLength={2}
                    className="min-h-11"
                    value={form.sender_country ?? "GB"}
                    onChange={(e) => updateSender({ sender_country: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>
            </section>

            <div className="flex justify-end">
              <Button
                type="submit"
                className="bg-gold-gradient hover:opacity-90 min-h-11 w-full sm:w-auto"
                disabled={save.isPending}
              >
                {save.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />
                ) : (
                  <Save className="h-4 w-4 mr-1" aria-hidden="true" />
                )}
                {t("shipping.savePreferences", "Save preferences")}
              </Button>
            </div>
          </form>

          {/* ------------------------------------------------------------------ */}
          {/* SECTION 2 — Advanced shipping integrations (Royal Mail Click&Drop) */}
          {/* ------------------------------------------------------------------ */}
          <Collapsible
            open={advancedOpen}
            onOpenChange={setAdvancedOpen}
            className="rounded-xl border border-border/60"
          >
            <CollapsibleTrigger
              className="w-full flex items-center justify-between gap-3 p-5 sm:p-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
              aria-label="Advanced shipping integrations"
            >
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-xl">{t("shipping.advancedHeading", "Advanced shipping integrations")}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t("shipping.advancedDesc", "Optional. Connect a third-party postage account for integrated label creation.")}
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="hidden sm:inline-flex">{statusBadge}</span>
                <ChevronDown
                  className={`h-5 w-5 text-muted-foreground transition-transform ${advancedOpen ? "rotate-180" : ""}`}
                  aria-hidden="true"
                />
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent className="px-5 sm:px-6 pb-6 space-y-5 border-t border-border/60">
              <div className="pt-5 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="font-display text-lg flex items-center gap-2">
                    <PlugZap className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {t("shipping.royalMailTitle", "Royal Mail Click & Drop")}
                  </h3>
                  <span className="sm:hidden">{statusBadge}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("shipping.royalMailDesc", "Connect your Royal Mail Click & Drop account to generate shipping labels directly from BidNova. Shipping labels are billed directly to your Royal Mail account. BidNova does not charge or store Royal Mail postage fees.")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("shipping.royalMailOptionalNote", "This integration is optional and intended for sellers who already use Royal Mail Click & Drop.")}
                </p>
              </div>

              {/* State-specific content */}
              {isConnected ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3"
                >
                  <p className="text-sm text-foreground">
                    {t("shipping.connectedMessage", "Your Royal Mail Click & Drop account is connected.")}
                  </p>
                  {form.api_key_last4 && (
                    <p className="text-xs text-muted-foreground break-words">
                      {t("shipping.onFile", "On file")}: <span className="font-mono">••••{form.api_key_last4}</span>
                      {form.last_validated_at && <> · {t("shipping.lastChecked", "last checked")} {new Date(form.last_validated_at).toLocaleString()}</>}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11 w-full sm:w-auto"
                      onClick={() => test.mutate()}
                      disabled={test.isPending}
                    >
                      {test.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />
                      ) : null}
                      {t("shipping.testConnection", "Test connection")}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="min-h-11 w-full sm:w-auto text-red-300 hover:text-red-200"
                      onClick={() => setConfirmDisconnect(true)}
                      disabled={disconnect.isPending}
                    >
                      {t("shipping.disconnect", "Disconnect")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {isInvalid && (
                    <div
                      role="alert"
                      className="rounded-md border border-red-500/40 bg-red-500/5 p-4 space-y-1"
                    >
                      <p className="text-sm font-medium text-red-300">{t("shipping.invalidTitle", "Connection unsuccessful")}</p>
                      <p className="text-xs text-muted-foreground">
                        {t("shipping.invalidDesc", "We couldn't verify your Royal Mail Click & Drop account. Please check your API key and try again.")}
                      </p>
                    </div>
                  )}

                  {!isInvalid && (
                    <p className="text-sm text-muted-foreground">
                      {t("shipping.notConnectedNote", "You can sell on BidNova without connecting Royal Mail. Connect Click & Drop only if you want integrated label creation.")}
                    </p>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="rm-api-key" className="flex items-center gap-1">
                      <KeyRound className="h-3 w-3" aria-hidden="true" />
                      {t("shipping.apiKeyLabel", "Royal Mail Click & Drop API key")}
                    </Label>
                    <Input
                      id="rm-api-key"
                      type="password"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      className="min-h-11 font-mono"
                      placeholder="rm_live_••••••••••"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      aria-describedby="rm-api-key-help"
                    />
                    <p id="rm-api-key-help" className="text-xs text-muted-foreground">
                      {t("shipping.apiKeyHelp", "Your API key is encrypted when stored and is never shown in full after saving.")}
                    </p>
                  </div>

                  <Button
                    type="button"
                    onClick={() => connect.mutate()}
                    disabled={connect.isPending || apiKey.trim().length < 10}
                    className="bg-gold-gradient hover:opacity-90 min-h-11 w-full sm:w-auto"
                  >
                    {connect.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />
                        {t("shipping.connecting", "Connecting…")}
                      </>
                    ) : (
                      t("shipping.connectVerify", "Connect & verify")
                    )}
                  </Button>
                </div>
              )}

              {/* Future provider placeholders — UI only, no API calls */}
              <div className="pt-5 border-t border-border/60">
                <FutureProviderCards />
              </div>
            </CollapsibleContent>
          </Collapsible>

        </div>
      )}

      <AlertDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("shipping.disconnectDialogTitle", "Disconnect Royal Mail?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("shipping.disconnectDialogDesc", "Disconnecting will stop BidNova from creating Royal Mail labels. Existing labels and previous orders will not be affected.")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("shipping.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                disconnect.mutate();
                setConfirmDisconnect(false);
              }}
              className="bg-red-600 hover:bg-red-500"
            >
              {t("shipping.disconnect", "Disconnect")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
